import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getEventLoggedInUserProfile, type Event } from "@/fetchers/events-v2";
import {
  Exhibitor,
  User,
  ExhibitorTranslations,
  ParticipantGroupType,
  type Language,
  type EventNetworkingSettings,
  type EventTranslations,
  type Profile,
  Ticket,
} from "database";
import { useSession } from "@sajilni/auth";
import { useSearchParams } from "next/navigation";
import {
  getSingleSkippableCheckoutTicket,
  type EventPageTicketForSkip,
} from "@/helper/getSingleSkippableCheckoutTicket";

type EventContextType = {
  event:
    | (Event & {
        translations: EventTranslations[];
      })
    | null;
  languages: Language[];
  eventNetworkingSettings: EventNetworkingSettings | null;
  /** Single purchasable ticket (min/max = 1) → skip ticket list; derived from event.tickets in context. */
  hasSinglePublicTicket?: boolean;
  singleSkippablePublicTicket: Ticket | null;
  exhibitor:
    | (Exhibitor & {
        team: User[];
        translations: (ExhibitorTranslations & { language: Language })[];
      })
    | null;
  userRole: ParticipantGroupType | null;
  loggedInUserProfile: Profile | null;
  setLoggedInUserProfile: React.Dispatch<React.SetStateAction<Profile | null>>;
};

const EventContext = createContext<EventContextType>({
  event: null,
  languages: [],
  eventNetworkingSettings: null,
  hasSinglePublicTicket: false,
  singleSkippablePublicTicket: null,
  exhibitor: null,
  userRole: null,
  loggedInUserProfile: null,
  setLoggedInUserProfile: () => {},
});

interface EventProviderProps {
  children: React.ReactNode;
  event: Event & {
    translations: EventTranslations[];
  };
  languages: Language[];
  eventNetworkingSettings: EventNetworkingSettings | null;
  exhibitor:
    | (Exhibitor & {
        team: User[];
        translations: (ExhibitorTranslations & { language: Language })[];
      })
    | null;
  userRole: ParticipantGroupType | null;
  loggedInUserProfile: Profile | null;
}

export const EventProvider = ({
  children,
  event,
  languages,
  eventNetworkingSettings,
  exhibitor,
  userRole,
  loggedInUserProfile: loggedInUserProfileInitial,
}: EventProviderProps) => {
  const [loggedInUserProfile, setLoggedInUserProfile] =
    useState<Profile | null>(loggedInUserProfileInitial);
  const { data: session } = useSession();
  const searchParams = useSearchParams();
  const token = searchParams?.get("token");

  // This effect is responsible for keeping the loggedInUserProfile state in sync with the current session and event.
  useEffect(() => {
    // If there's a logged-in user (session.user.id exists)
    // and no profile has been loaded yet (loggedInUserProfile is null)
    // and the current event is not yet associated with the loaded profile (event.id !== loggedInUserProfile?.eventId):
    if (
      session?.user?.id &&
      !loggedInUserProfile &&
      event.id !== (loggedInUserProfile as unknown as Profile)?.eventId
    ) {
      getEventLoggedInUserProfile(event.id, session.user.id).then((profile) => {
        setLoggedInUserProfile(profile); // Update local state with the fetched profile
      });
    }
    // If there is no logged-in user (session.user.id is falsy)
    // but we still have a loggedInUserProfile in state:
    else if (!session?.user?.id && loggedInUserProfile) {
      // Ensure local state is reset to null since there's no authenticated user
      setLoggedInUserProfile(null);
    }
  }, [session?.user?.id, loggedInUserProfile, event.id]);

  const { hasSinglePublicTicket, singleSkippablePublicTicket } = useMemo(() => {
    const single = getSingleSkippableCheckoutTicket(
      event.tickets as EventPageTicketForSkip[],
      { token: token ?? null }
    );
    return {
      hasSinglePublicTicket: single !== null,
      singleSkippablePublicTicket: single as Ticket | null,
    };
  }, [token, event.tickets]);

  const updatedEventNetworkingSettings = {
    ...eventNetworkingSettings,
  } as EventNetworkingSettings;

  const contextValue = {
    event,
    languages,
    hasSinglePublicTicket,
    singleSkippablePublicTicket,
    exhibitor,
    userRole,
    eventNetworkingSettings: updatedEventNetworkingSettings,
    loggedInUserProfile,
    setLoggedInUserProfile,
  };

  return (
    <EventContext.Provider value={contextValue}>
      {children}
    </EventContext.Provider>
  );
};

/**
 * Custom hook to access the Event context
 * @throws Error if used outside of EventProvider
 */
export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error("useEvent must be used within a EventProvider");
  }
  return context;
};
